import './SegmentationIdCreation.scss';
import * as R from 'ramda';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { MdAdd, MdCheck, MdContentCopy, MdDelete } from 'react-icons/md';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Design, Intent, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { DropdownVariant } from '@Components/Dropdown/dropdown-constants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { PendingChangesControls } from '@Components/PendingChanges';
import { FullPageSpinner } from '@Components/Spinner';
import { BPMTable, Header } from '@Components/StickyTable';
import { useSetError } from '../../redux/modals';
import { downloadPNG, exportToExcel } from '../../utils/download-utils';
import {
  awaitJSON,
  SegmentationMappingLambdaFetch,
  SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
} from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { CustomSegmentsData } from './segmentation-mapping-constants';

const TabOptions = {
  CREATE_NEW_IDS: 'Create new ID',
  EXISTING_IDS: 'Existing IDs'
} as const;
type TabOptionsType = (typeof TabOptions)[keyof typeof TabOptions];

interface SegmentationIdCreationProps {
  customSegments: CustomSegmentsData[] | undefined;
}

type CustomSegmentMapFromNumberToName = Record<number, string>;

interface GenerateOrFetchSegmentationId {
  segmentationId: string;
  isNew: boolean;
  segmentMap: Record<string, number | string>;
}

interface SegmentationIdRow {
  segmentationId: string;
  [key: string]: string | number | null | undefined;
}

const renderNoRowsAddNewIds = () => <div className='no-rows'>Add new segmentation IDs.</div>;

const makeInitNewRow = (
  customSegments: CustomSegmentsData[] | undefined
): Record<string, unknown> =>
  (customSegments || []).reduce((acc, curr) => ({ ...acc, [curr.segmentName]: null }), {});

const areAllSegmentValuesNotNull = (
  tableRows: Record<string, unknown>[],
  uniqueCustomSegmentsNames: string[]
) => {
  const allValuesFilled = tableRows.every(row =>
    uniqueCustomSegmentsNames.every(
      segmentName => row[segmentName] !== null && row[segmentName] !== undefined
    )
  );
  if (!allValuesFilled) {
    return false;
  }
  const uniqueRows = new Set(
    tableRows.map(row => uniqueCustomSegmentsNames.map(segmentName => row[segmentName]).join('|'))
  );

  const noDuplicates = uniqueRows.size === tableRows.length;

  return allValuesFilled && noDuplicates && tableRows.length > 0;
};

export const SegmentationIdCreation = ({ customSegments }: SegmentationIdCreationProps) => {
  const setError = useSetError();
  const { cid } = useCompanyInfo();
  const [newRow, setNewRow] = useState<Record<string, unknown>>({});
  const [tableRows, setTableRows] = useState<Record<string, unknown>[]>([]);
  const [focusedCell, setFocusedCell] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabOptionsType>(TabOptions.CREATE_NEW_IDS);
  const [newId, setNewId] = useState<GenerateOrFetchSegmentationId[]>([]);
  const [uniqueCustomSegmentsName, setUniqueCustomSegmentsName] = useState<string[]>([]);
  const [segmentationIds, setSegmentationIds] = useState<SegmentationIdRow[] | undefined>();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [customSegmentNameMapWordToNumber, setCustomSegmentNameMapWordToNumber] =
    useState<CustomSegmentMapFromNumberToName>({});
  const [customSegmentValueMapNumberToWord, setCustomSegmentValueMapNumberToWord] =
    useState<CustomSegmentMapFromNumberToName>({});

  const fetchSegmentationIds = useCallback(async () => {
    try {
      const res = await SegmentationMappingLambdaFetch('/getSegmentationIds', {
        params: { company: cid }
      });
      const data = await awaitJSON<SegmentationIdRow[]>(res);
      setSegmentationIds(data);
    } catch (e) {
      setError({
        message: `Failed to get segmentation IDs. Error: ${e.message}`,
        reportError: e
      });
    }
  }, [cid, setError]);

  useEffect(() => {
    fetchSegmentationIds();
  }, [fetchSegmentationIds]);

  useEffect(() => {
    if (customSegments) {
      const segmentNames = customSegments.map(segment => segment.segmentName);
      setUniqueCustomSegmentsName(segmentNames);
    }
  }, [customSegments]);

  useEffect(() => {
    if (customSegments) {
      const initNewRow = makeInitNewRow(customSegments);

      const segmentNamesMapWordToNumber = customSegments.reduce(
        (acc, curr) => ({ ...acc, [curr.segmentName]: curr.segmentId }),
        {} as CustomSegmentMapFromNumberToName
      );

      const segmentValuesMapNumberToWord = customSegments.reduce((acc, curr) => {
        const valuesMap = curr.values.reduce(
          (map, { valueId, valueName }) => ({ ...map, [valueId]: valueName }),
          {} as CustomSegmentMapFromNumberToName
        );
        return { ...acc, ...valuesMap };
      }, {} as CustomSegmentMapFromNumberToName);

      setCustomSegmentNameMapWordToNumber(segmentNamesMapWordToNumber);
      setCustomSegmentValueMapNumberToWord(segmentValuesMapNumberToWord);
      setTableRows([initNewRow]);
      setNewRow(initNewRow);
    }
  }, [setError, cid, customSegments]);

  const excelDownloadSegmentationIds = useCallback(() => {
    exportToExcel(segmentationIds || [], `${cid}_segmentation_ids`);
  }, [segmentationIds, cid]);

  const pngDownloadSegmentationIds = useCallback(async () => {
    await downloadPNG('.bpm-table-container .sticky-table-outer', `${cid}_segmentation_ids`);
  }, [cid]);

  const dropdownOptions = useMemo(() => {
    if (!customSegments) {
      return {};
    }
    const options: Record<string, { label: string; value: string }[]> = {};
    for (const segment of customSegments) {
      options[segment.segmentName] = segment.values.map(value => ({
        label: value.valueName,
        value: String(value.valueId)
      }));
    }
    return options;
  }, [customSegments]);

  const segmentValueIdToName = useMemo(() => {
    if (!customSegments) {
      return {};
    }
    const valueIdToName: Record<string, string> = {};
    for (const segment of customSegments) {
      for (const value of segment.values) {
        valueIdToName[value.valueId] = value.valueName;
      }
    }
    return valueIdToName;
  }, [customSegments]);

  const makeTableHeader = useCallback(
    (segment: CustomSegmentsData): Header => {
      const { segmentName } = segment;
      return {
        label: segmentName,
        name: segmentName,
        flex: 1,
        renderer: (row: Record<string, unknown>, i: number) => {
          const rawValue = row[segmentName];
          const value = rawValue == null ? '' : String(rawValue);
          const label = segmentValueIdToName[value];
          const focusedCellKey = `${segmentName}-${i}`;
          if (focusedCell === focusedCellKey) {
            return (
              <Dropdown
                size={Size.LG}
                searchable
                variant={DropdownVariant.CELL}
                className='select-container'
                menuPortalTarget={document.body}
                value={value || null}
                placeholder='Select value'
                onChange={selection =>
                  setTableRows(prevRows =>
                    prevRows.map((r, index) =>
                      index === i
                        ? { ...r, [segmentName]: selection ? Number(selection) : null }
                        : r
                    )
                  )
                }
                options={dropdownOptions[segmentName]}
                reactSelectProps={{
                  autoFocus: true,
                  defaultMenuIsOpen: true,
                  onBlur: () => setFocusedCell('')
                }}
              />
            );
          }
          return (
            <InteractiveDiv
              className='editable-cell'
              onClick={() => setFocusedCell(focusedCellKey)}
            >
              <span className='cell-text'>{label || '--'}</span>
            </InteractiveDiv>
          );
        }
      };
    },
    [dropdownOptions, focusedCell, segmentValueIdToName]
  );

  const tableHeaders: Header[] = useMemo(() => {
    if (!customSegments) {
      return [];
    }
    return customSegments.map(segment => makeTableHeader(segment));
  }, [makeTableHeader, customSegments]);

  const saveChanges = useCallback(async () => {
    try {
      setSaving(true);
      const newIds: GenerateOrFetchSegmentationId[] = [];
      for (const row of tableRows) {
        const filteredNulls: Record<string, unknown> = R.pickBy(R.complement(R.isNil), row);
        const transformedObject = Object.keys(filteredNulls).reduce(
          (acc, key) => {
            const newKey = customSegmentNameMapWordToNumber[key];
            if (newKey !== undefined) {
              acc[newKey] = filteredNulls[key];
            }
            return acc;
          },
          {} as Record<string, unknown>
        );
        // Sequential per-row loop — one row's ID must be generated/fetched before the next starts,
        // so this needs the short visibility-wait bound (see SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS's
        // comment) rather than the long default.
        const res = await SegmentationMappingLambdaFetch('/generateOrFetchSegmentationID', {
          method: 'POST',
          body: { company: cid, segmentMap: transformedObject },
          visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
        });
        const { segmentationId, isNew }: GenerateOrFetchSegmentationId = await awaitJSON(res);
        const segmentMapWithNames = Object.entries(filteredNulls).reduce(
          (acc, [key, value]) => {
            const segmentName: string = key;
            const segmentValue: string = customSegmentValueMapNumberToWord[Number(value)];

            return {
              ...acc,
              [segmentName]: segmentValue
            };
          },
          {} as Record<string, string>
        );
        newIds.push({ segmentationId, isNew, segmentMap: segmentMapWithNames });
      }

      setSaving(false);
      setNewId(newIds);
      if (newIds.some(item => item.isNew)) {
        fetchSegmentationIds();
      }
      const initNewRow = makeInitNewRow(customSegments);
      setNewRow(initNewRow);
      setTableRows([initNewRow]);
      setShowSuccessDialog(true);
    } catch (e) {
      setError({
        message: `Failed to save new segmentation ID. Error: ${e.message}`,
        reportError: e
      });
      setSaving(false);
    }
  }, [
    customSegments,
    customSegmentNameMapWordToNumber,
    tableRows,
    cid,
    customSegmentValueMapNumberToWord,
    fetchSegmentationIds,
    setError
  ]);

  const clearAllChanges = useCallback(() => {
    const initNewRow = makeInitNewRow(customSegments);
    setNewRow(initNewRow);
    setTableRows([initNewRow]);
  }, [customSegments]);

  const hasPendingChanges = useMemo(
    () =>
      tableRows.length > 1 ||
      tableRows.some(row => Object.values(row).some(value => value != null)),
    [tableRows]
  );

  const newIds = newId.filter(item => item.isNew);
  const existingIds = newId.filter(item => !item.isNew);

  let renderItem: ReactElement;
  switch (activeTab) {
    case TabOptions.CREATE_NEW_IDS:
      renderItem = (
        <div className='create-new-id-view'>
          <div className='explainer-row'>
            <div className='explainer'>
              Use this to create new Segmentation IDs that are derived from selecting a unique
              combination of segment values below.
            </div>
            <div className='new-id-buttons'>
              <Button
                size={Size.LG}
                design={Design.NEUTRAL}
                leadingElement={<MdContentCopy />}
                disabled={saving}
                onClick={() =>
                  setTableRows(prevRows => {
                    const lastRow = prevRows[prevRows.length - 1];
                    return lastRow ? [...prevRows, { ...lastRow }] : prevRows;
                  })
                }
              >
                Duplicate Last Row
              </Button>
              <Button
                size={Size.LG}
                design={Design.NEUTRAL}
                leadingElement={<MdDelete />}
                disabled={saving}
                onClick={() => setTableRows(prevRows => prevRows.slice(0, -1))}
              >
                Delete Last Row
              </Button>
              <Button
                size={Size.LG}
                leadingElement={<MdAdd />}
                disabled={saving}
                onClick={() => setTableRows(prevRows => [...prevRows, newRow])}
              >
                Add New Row
              </Button>
            </div>
          </div>
          <div className='new-segmentation-ids-table'>
            <BPMTable data={tableRows} headers={tableHeaders} filterBar={false} />
          </div>
        </div>
      );
      break;
    case TabOptions.EXISTING_IDS:
      renderItem = (
        <ExistingIds
          segments={(customSegments || []).map(row => row.segmentName)}
          segmentationIds={segmentationIds}
        />
      );
      break;
    default:
      renderItem = (
        <BPMTable
          noRowsRenderer={renderNoRowsAddNewIds}
          data={[newRow]}
          headers={tableHeaders}
          filterBar={false}
        />
      );
  }

  return (
    <div className='segmentation-id-creation'>
      <AlertDialog
        intent={Intent.SUCCESS}
        show={showSuccessDialog}
        onHide={() => {
          setNewId([]);
          setShowSuccessDialog(false);
        }}
        icon={<MdCheck size={95} />}
        title='Success!'
        body={
          <>
            {newId.length >= 1 && (
              <div className='new-id'>
                {newIds.length > 0 && (
                  <div className='spacing-below'>
                    Your new segmentation {newIds.length > 1 ? 'IDs are' : 'ID is'}{' '}
                    <strong>{newIds.map(item => item.segmentationId).join(', ')}</strong>. In the
                    relevant advertising platforms, paste this ID where you&apos;d normally define a
                    Campaign, Ad Group, Ad, or equivalent. This will automatically tag the media
                    with the segment values that you selected. You can view all IDs on the
                    &quot;Existing IDs&quot; tab.
                  </div>
                )}

                {existingIds.length > 0 &&
                  existingIds.map(item => (
                    <div key={item.segmentationId} className='spacing-below'>
                      The following id, <strong>{item.segmentationId}</strong>, already exists with
                      the following segments:
                      {Object.entries(item.segmentMap).map(([key, value]) => (
                        <div key={key}>
                          {key}: {value}
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            )}
          </>
        }
      />
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          if (!value || value === activeTab) {
            return;
          }
          const isTabOption = (v: string): v is TabOptionsType =>
            (Object.values(TabOptions) as string[]).includes(v);
          if (isTabOption(value)) {
            setActiveTab(value);
          }
        }}
      >
        <Nav.Item key={TabOptions.CREATE_NEW_IDS}>
          <Nav.Link eventKey={TabOptions.CREATE_NEW_IDS}>{TabOptions.CREATE_NEW_IDS}</Nav.Link>
        </Nav.Item>
        <Nav.Item key={TabOptions.EXISTING_IDS}>
          <Nav.Link eventKey={TabOptions.EXISTING_IDS}>{TabOptions.EXISTING_IDS}</Nav.Link>
        </Nav.Item>
      </Nav>
      <div className='segmentation-id-creation-body'>
        {activeTab === TabOptions.EXISTING_IDS && (
          <div className='controls'>
            <DownloadDropdown
              onClickOptions={[excelDownloadSegmentationIds, pngDownloadSegmentationIds]}
            />
          </div>
        )}
        {Object.keys(newRow).length === 0 ? <FullPageSpinner /> : renderItem}
        {activeTab === TabOptions.CREATE_NEW_IDS && (
          <div className='controls'>
            <PendingChangesControls
              hasPendingChanges={hasPendingChanges}
              saveChanges={saveChanges}
              isSaving={saving}
              clearAllChanges={clearAllChanges}
              disableSaveButton={!areAllSegmentValuesNotNull(tableRows, uniqueCustomSegmentsName)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface ExistingIdsProps {
  segments: string[];
  segmentationIds: SegmentationIdRow[] | undefined;
}

const ExistingIds = ({ segments, segmentationIds }: ExistingIdsProps) => {
  const tableHeaders: Header[] = useMemo(() => {
    if (!segments) {
      return [];
    }
    const headers: Header[] = [
      {
        label: 'Segmentation ID',
        name: 'segmentationId',
        flex: 1,
        renderer: data => <div className='segmentation-id-cell'>{data.segmentationId}</div>
      }
    ];
    segments.forEach(segment =>
      headers.push({
        label: segment,
        name: segment,
        flex: 1
      })
    );
    return headers;
  }, [segments]);

  if (!segmentationIds) return <FullPageSpinner />;
  return <BPMTable data={segmentationIds} headers={tableHeaders} />;
};
